import { api } from "./api";

/**
 * Shared plumbing for the printable Vehicle Release / Sign-Over form
 * (`GET /jobs/{id}/release-form`). The server returns a self-contained HTML
 * document that calls `window.print()` on load and, for every action except
 * `disposed`, renders the page TWICE — "Customer Copy" then "Office Copy".
 *
 * Fetched through the shared axios client so the bearer token is attached
 * (the endpoint is admin/dispatcher only), then handed to a tab as a Blob URL.
 */

export async function fetchReleaseFormHtml(jobId, action) {
  const r = await api.get(`/jobs/${jobId}/release-form`, {
    params: { action },
    responseType: "text",
  });
  return r.data;
}

/**
 * Render fetched HTML into a tab. Pass `preOpened` — a tab opened
 * synchronously inside the click handler — when the fetch happens after an
 * await: popup blockers reject `window.open` once the user gesture has been
 * broken by an async call, which silently loses the print.
 */
export function renderReleaseFormInTab(html, preOpened) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const tab = preOpened || window.open(url, "_blank", "noopener");
  if (preOpened) preOpened.location.replace(url);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return tab;
}

/** FastAPI returns JSON errors even when the happy path is HTML. */
export function releaseFormError(e, fallback = "Failed to open release form") {
  if (e?.response?.data?.detail) return e.response.data.detail;
  if (typeof e?.response?.data === "string") {
    try {
      return JSON.parse(e.response.data).detail || fallback;
    } catch {
      return fallback;
    }
  }
  return e?.message || fallback;
}
